import { Button, cn } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { translateErrorCode } from '@/lib/errors/messages';
import { toast } from '@/lib/toast';

type Props = {
  code?: string | undefined;
  id?: string | undefined;
  details?: Record<string, unknown> | undefined;
  variant?: 'block' | 'compact';
};

/**
 * The single component for surfacing a reportable error, per ADR 0014. Shows
 * the translated message and, when an `error.id` is present, the id in a
 * copyable form. The id is always visible (no disclosure) — it is the user's
 * only handle on a reported incident.
 */
export function ErrorWithId({ code, id, details, variant = 'block' }: Props) {
  const t = useTranslations();
  const message = translateErrorCode(t, code, details);

  const copyId = async () => {
    if (id === undefined) return;
    await navigator.clipboard.writeText(id);
    toast.success(t('errors.support.failure-id-copied'));
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn('flex flex-col gap-2', variant === 'block' ? 'text-base' : 'text-base-sm')}
    >
      <p className="text-text-primary">{message}</p>
      {id !== undefined && (
        <p className="flex items-center gap-1 font-mono text-base-sm text-text-tertiary">
          <span>{t('errors.support.failure-id-prefix')}:</span>
          <span data-testid="error-id">{id}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-control-sm w-control-sm"
            aria-label={t('errors.support.copy-failure-id')}
            onClick={copyId}
          >
            <CopyIcon />
          </Button>
        </p>
      )}
    </div>
  );
}

function CopyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}
